/**
 * Signals #3-#6 — experience, budget, location, deadline.
 *
 * Pure functions, no side-effects, no Supabase imports.  Each returns
 * 0-100 and uses the sentinel value 50 for "input unavailable" (the
 * inference engine then redistributes the dimension's weight).
 */

// ─── Experience alignment ─────────────────────────────────────────────────────

/**
 * Gaussian curve centred on the midpoint of [jobMin, jobMax].
 * Returns 0-100. Either side null → 50 (neutral).
 */
export function experienceScore(
  userYears: number | null | undefined,
  jobMin: number | null | undefined,
  jobMax: number | null | undefined,
): number {
  if (userYears == null || (jobMin == null && jobMax == null)) return 50;

  const min = jobMin ?? 0;
  const max = jobMax ?? min;
  const midpoint = (min + max) / 2;
  const spread = Math.max(max - min, 2); // avoid zero spread

  const diff = userYears - midpoint;
  const sigma = spread / 2;
  const score = 100 * Math.exp(-0.5 * Math.pow(diff / sigma, 2));

  return Math.round(Math.max(0, Math.min(100, score)));
}

// ─── Budget / CTC fit ─────────────────────────────────────────────────────────

/**
 * - Within the job range → 100
 * - Within 20% of the boundary → 70 → 30..40 (linear falloff)
 * - Beyond → proportional decrease toward 0
 * - Either side null → 50 (neutral)
 */
export function budgetScore(
  userExpected: number | null | undefined,
  jobMin: number | null | undefined,
  jobMax: number | null | undefined,
): number {
  if (userExpected == null || (jobMin == null && jobMax == null)) return 50;

  const min = jobMin ?? 0;
  const max = jobMax ?? min;

  if (userExpected >= min && userExpected <= max) return 100;

  const rangeWidth = Math.max(max - min, 1);
  const tolerance = rangeWidth * 0.2;

  if (userExpected < min) {
    const gap = min - userExpected;
    if (gap <= tolerance) return Math.round(70 - (gap / tolerance) * 30); // 70→40
    return Math.max(
      0,
      Math.round(40 - ((gap - tolerance) / (min * 0.5 + 1)) * 40),
    );
  }
  const gap = userExpected - max;
  if (gap <= tolerance) return Math.round(70 - (gap / tolerance) * 40); // 70→30
  return Math.max(
    0,
    Math.round(30 - ((gap - tolerance) / (max * 0.3 + 1)) * 30),
  );
}

// ─── Location fit ─────────────────────────────────────────────────────────────

/**
 * - Remote job → 100
 * - User's preferred locations contain the job location → 100
 * - User's current location matches (case-insensitive substring) → 100
 * - Hybrid → 60
 * - No job location specified → 50 (neutral)
 * - Else → 30
 */
export function locationScore(
  userLocation: string | null | undefined,
  userPreferred: string[] | null | undefined,
  jobLocation: string | null | undefined,
  remotePolicy: 'onsite' | 'hybrid' | 'remote' | null | undefined,
): number {
  if (remotePolicy === 'remote') return 100;
  if (jobLocation == null) return 50;

  const jobLoc = jobLocation.toLowerCase().trim();

  if (Array.isArray(userPreferred) && userPreferred.length > 0) {
    const match = userPreferred.some((pl) => {
      const p = pl.toLowerCase().trim();
      return p === jobLoc || p.includes(jobLoc) || jobLoc.includes(p);
    });
    if (match) return 100;
  }

  if (userLocation) {
    const cLoc = userLocation.toLowerCase().trim();
    if (cLoc === jobLoc || cLoc.includes(jobLoc) || jobLoc.includes(cLoc))
      return 100;
  }

  if (remotePolicy === 'hybrid') return 60;
  return 30;
}

// ─── Role / function alignment ────────────────────────────────────────────────

/**
 * Classifies a free-text job title or candidate headline into a broad
 * career-function bucket.  Returns 'unknown' when the text doesn't match
 * any recognised pattern — callers treat this as the NEUTRAL sentinel (50)
 * so no false penalties are applied.
 */
export function classifyJobFunction(
  text: string | null | undefined,
): 'product' | 'management' | 'engineering' | 'design' | 'data' | 'sales' | 'marketing' | 'hr' | 'operations' | 'unknown' {
  const t = (text ?? '').toLowerCase();
  if (!t) return 'unknown';

  // Product — checked before management to catch "product manager" before "manager"
  if (/\b(product manager|product owner|\bpm\b|program manager|scrum master|agile coach)\b/.test(t)) return 'product';
  // Management
  if (/\b(director|vp\b|vice president|head of|chief|\bcto\b|\bceo\b|\bcoo\b|svp|evp|engineering manager|senior manager|associate director|general manager|technical manager)\b/.test(t)) return 'management';
  // Engineering / IC tech — checked after management so "engineering manager" → management
  if (/\b(software engineer|developer|programmer|\bsde\b|\bswe\b|devops|\bsre\b|backend|frontend|fullstack|full.stack|mobile developer|\bios\b|\bandroid\b|platform engineer|ml engineer|machine learning engineer|data engineer|cloud engineer|security engineer|qa engineer|test engineer|\bit engineer\b)\b/.test(t)) return 'engineering';
  // Design — fashion/creative before generic "designer" (handles common "desinger" typo)
  if (/\b(fashion|apparel|textile|garment|stylist|merchandis|costume|clothing|wardrobe|desinger)\b/.test(t)) return 'design';
  if (/\b(designer|\bux\b|\bui\b|visual design|product designer|graphic designer|illustrator|creative director)\b/.test(t)) return 'design';
  // Data / Analytics
  if (/\b(data analyst|business analyst|\bbi\b|business intelligence|tableau|power bi|data scientist)\b/.test(t)) return 'data';
  // Sales / Revenue
  if (/\b(sales|account executive|account manager|business development|\bbd\b|revenue|customer success)\b/.test(t)) return 'sales';
  // Marketing — explicitly excluding "product marketing" so it stays neutral (closer to product)
  if (/\b(digital marketing|seo specialist|sem specialist|ppc|social media manager|brand manager|email marketing|affiliate marketing|content marketing|marketing specialist|marketing executive|marketing analyst|marketing manager|marketing coordinator|growth hacker|performance marketing|campaign manager|demand generation)\b/.test(t)) return 'marketing';
  // HR / People
  if (/\b(human resources|\bhr\b|recruiter|talent acquisition|people ops|people operations|hrbp|hr manager|hr executive|hr generalist|hr specialist)\b/.test(t)) return 'hr';
  // Operations / Supply Chain
  if (/\b(operations manager|supply chain|logistics|operations executive|operations analyst|business operations|process improvement|project coordinator)\b/.test(t)) return 'operations';

  return 'unknown';
}

/** Pairwise function-compatibility matrix (0-100). */
const ROLE_COMPAT: Record<string, Record<string, number>> = {
  product:     { product: 100, management: 70, engineering: 20, design: 60, data: 55, sales: 35, marketing: 25, hr: 10, operations: 45, unknown: 50 },
  management:  { management: 100, product: 70, engineering: 60, design: 55, data: 55, sales: 50, marketing: 50, hr: 45, operations: 60, unknown: 60 },
  engineering: { engineering: 100, management: 55, product: 45, design: 40, data: 70, sales: 20, marketing: 15, hr: 10, operations: 35, unknown: 60 },
  design:      { design: 100, product: 70, engineering: 22, management: 50, data: 40, sales: 30, marketing: 55, hr: 10, operations: 25, unknown: 55 },
  data:        { data: 100, engineering: 65, product: 55, management: 50, design: 30, sales: 30, marketing: 40, hr: 20, operations: 40, unknown: 55 },
  sales:       { sales: 100, management: 65, product: 50, engineering: 15, design: 25, data: 30, marketing: 60, hr: 20, operations: 40, unknown: 50 },
  marketing:   { marketing: 100, sales: 60, design: 55, product: 25, data: 40, management: 50, engineering: 15, hr: 20, operations: 35, unknown: 50 },
  hr:          { hr: 100, management: 55, operations: 45, product: 10, engineering: 10, design: 10, data: 25, sales: 20, marketing: 20, unknown: 50 },
  operations:  { operations: 100, management: 65, product: 45, data: 40, engineering: 35, sales: 40, marketing: 35, hr: 45, design: 20, unknown: 55 },
  unknown:     { product: 50, management: 50, engineering: 50, design: 50, data: 50, sales: 50, marketing: 50, hr: 50, operations: 50, unknown: 50 },
};

/**
 * Returns 0-100 representing how well the candidate's career function matches
 * the job's function.
 *
 * • Returns the NEUTRAL sentinel (50) when EITHER side cannot be classified —
 *   the inference engine will redistribute that weight to the remaining signals
 *   rather than penalising a candidate based on an ambiguous title.
 * • Returns < 30 for clear cross-function mismatches (PM → Marketing, Engineer → HR).
 *   The ranking engine uses this to apply an additional penalty so mismatched jobs
 *   are never surfaced as "good_fit".
 */
/** Fashion/apparel/creative profiles should not surface software or IT roles. */
function isFashionCreativeProfile(text: string): boolean {
  return /\b(fashion|apparel|textile|garment|stylist|merchandis|costume|clothing|wardrobe|nift|desinger|designing clothes)\b/i.test(
    text,
  );
}

export function roleAlignmentScore(
  userHeadline: string | null | undefined,
  userDesiredRoles: string[] | null | undefined,
  jobTitle: string | null | undefined,
): number {
  const jobFunc = classifyJobFunction(jobTitle);

  // Prefer explicit desired_roles over the headline when set
  const combined = [
    ...(userDesiredRoles ?? []),
    userHeadline ?? '',
  ].join(' ');
  const userFunc = classifyJobFunction(combined);

  if (userFunc === 'unknown' && isFashionCreativeProfile(combined)) {
    if (jobFunc === 'engineering') return 12;
    if (jobFunc === 'product' || jobFunc === 'data') return 18;
    if (jobFunc === 'marketing') return 28;
    if (jobFunc === 'design') return 85;
    if (jobFunc === 'unknown') return 50;
  }

  if (userFunc === 'unknown' || jobFunc === 'unknown') return 50; // NEUTRAL sentinel

  let score = ROLE_COMPAT[userFunc]?.[jobFunc] ?? 50;

  if (userFunc === 'design' && isFashionCreativeProfile(combined)) {
    if (jobFunc === 'engineering') score = Math.min(score, 15);
    else if (jobFunc === 'product' || jobFunc === 'data') score = Math.min(score, 22);
  }

  return score;
}

// ─── Deadline feasibility ─────────────────────────────────────────────────────

/**
 * - notice_period_days <= days_remaining → 100
 * - within 7 days of deadline → 60
 * - beyond → proportional decrease from 50
 * - Either side null → 50 (neutral)
 */
export function deadlineScore(
  noticePeriodDays: number | null | undefined,
  hiringDeadline: string | null | undefined,
): number {
  if (noticePeriodDays == null || hiringDeadline == null) return 50;

  const now = Date.now();
  const deadline = new Date(hiringDeadline).getTime();
  if (Number.isNaN(deadline)) return 50;
  const daysRemaining = Math.round((deadline - now) / 86_400_000);

  if (daysRemaining <= 0) return 50;

  const gap = noticePeriodDays - daysRemaining;
  if (gap <= 0) return 100;
  if (gap <= 7) return 60;

  const penalty = Math.min(50, Math.round((gap / 10) * 10));
  return Math.max(0, 50 - penalty);
}
